import { useEffect, useState } from 'preact/hooks';
import { net, profile, roles } from '../store';
import { UGC_MAX_BYTES, UGC_MAX_SIDE, removeCustomPart, ugcUrl, uploadCustomPart, type UgcPart } from '../ugc';

const PART_COPY: Record<UgcPart, { title: string; hint: string }> = {
  body: { title: 'Body', hint: 'Torso only, facing right; the mole keeps its own legs and arms. Drawn into a box about 26×22.' },
  head: { title: 'Head', hint: 'The whole head, facing right, with its snout on the right. Drawn into a circle about 32 across.' },
};

/** Locker tab for players with the creator role: upload a PNG body and head of their own. */
export function CreatorPanel() {
  const p = profile.value;
  if (!p) return null;
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <p class="muted" style={{ fontSize: 13 }}>
        Your own character art, shown to everyone in the lobby and in matches. Transparent PNG, up to {UGC_MAX_SIDE}×{UGC_MAX_SIDE} and{' '}
        {Math.round(UGC_MAX_BYTES / 1024)} KB per part. Keep it something you would want a whole voice channel to see.
      </p>
      <div class="ugc-grid">
        {(['body', 'head'] as UgcPart[]).map((part) => (
          <UploadCard key={part} part={part} current={p.equipped.custom?.[part]} />
        ))}
      </div>
    </div>
  );
}

function UploadCard({ part, current }: { part: UgcPart; current?: string }) {
  const [busy, setBusy] = useState(false);
  const copy = PART_COPY[part];
  const onFile = async (e: Event) => {
    const input = e.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    setBusy(true);
    await uploadCustomPart(part, file);
    setBusy(false);
  };
  return (
    <div class="ugc-card">
      <div class="ugc-preview" aria-label={current ? `Current ${part}` : `No custom ${part}`}>
        {current ? <img src={ugcUrl(current)} alt="" /> : <span class="muted">Drawn mole</span>}
      </div>
      <div class="ugc-body">
        <b>{copy.title}</b>
        <span class="hint">{copy.hint}</span>
        <div class="ugc-actions">
          <label class={`btn small${busy ? ' disabled' : ''}`}>
            {busy ? 'Uploading…' : current ? 'Replace PNG' : 'Upload PNG'}
            <input type="file" accept="image/png" disabled={busy} onChange={onFile} style={{ display: 'none' }} />
          </label>
          {current && (
            <button class="btn small ghost" disabled={busy} onClick={() => void removeCustomPart(part)}>
              Remove
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** Locker tab for admins (ADMIN_USER_IDS on the server): grant the creator role by Discord user id. */
export function AdminPanel() {
  const [userId, setUserId] = useState('');
  const r = roles.value;
  useEffect(() => {
    net.value?.send({ t: 'listRoles' });
  }, []);
  const grant = () => {
    const id = userId.trim();
    if (!id) return;
    net.value?.send({ t: 'setRole', userId: id, role: 'creator', granted: true });
    setUserId('');
  };
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <p class="muted" style={{ fontSize: 13 }}>
        Creators can upload their own character body and head. Paste a Discord user id (Developer Mode → right-click a user → Copy User ID). Guests
        testing in a browser have ids like <code>dev:dev-name</code>.
      </p>
      <form
        class="role-form"
        onSubmit={(e) => {
          e.preventDefault();
          grant();
        }}
      >
        <input
          value={userId}
          onInput={(e) => setUserId((e.currentTarget as HTMLInputElement).value)}
          placeholder="Discord user id"
          inputMode="numeric"
          aria-label="Discord user id"
        />
        <button class="btn primary small" type="submit" disabled={!userId.trim()}>
          Grant creator
        </button>
      </form>
      <div class="role-list">
        {r.creators.length === 0 ? (
          <p class="muted" style={{ fontSize: 13 }}>No creators yet.</p>
        ) : (
          r.creators.map((c) => (
            <div key={c.id} class="role-row">
              <span>
                <b>{c.name ?? 'Unknown mole'}</b> <span class="muted num">{c.id}</span>
              </span>
              <button class="btn small ghost" onClick={() => net.value?.send({ t: 'setRole', userId: c.id, role: 'creator', granted: false })}>
                Revoke
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
